import { useState } from 'react'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import { NextLink } from '@/features/shared/elements/Link'
import { type UniversalCategory } from '@/features/shop/services/UniversalCategories/types'
import { useMarketingZip } from '@/features/shop/components/UngatedCategoryPage/hooks'
import {
  urlBuilder,
  getUniversalCategories,
} from '@/features/shop/services/UniversalCategories/utils'
import { Body } from '@shipt/design-system-typography'
import { AccordionItem } from '@shipt/design-system-accordion'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { VisuallyHidden } from '@/features/shared/elements/VisuallyHidden'

type Props = {
  category: UniversalCategory
  currentCategory: UniversalCategory | undefined
}

export const CategoryNavAccordionItem = ({ category, currentCategory }: Props) => {
  const zip = useMarketingZip()
  const universalCategories = getUniversalCategories()
  const isCategorySelected = category.id === currentCategory?.id
  const isAnySubcategorySelected = Boolean(
    category.children?.find(
      ({ id }) => id === currentCategory?.parent_id || id === currentCategory?.id,
    ),
  )
  const [isOpen, setIsOpen] = useState(isCategorySelected || isAnySubcategorySelected)
  const { closeDialog } = useHandleDialog()

  const handleToggle = () => setIsOpen(!isOpen)

  return !isEmpty(category.children) ? (
    <AccordionItem
      title={category.name}
      headerAs="h2"
      id={currentCategory?.id}
      isExpanded={isOpen}
      onClick={handleToggle}
      shouldPersistContent
    >
      {category.children?.map((l2_category) => {
        const { id, name } = l2_category
        const isSubcategorySelected =
          id === currentCategory?.parent_id || id === currentCategory?.id

        return (
          <SubcategoryLinkWrapper key={id} isSelected={isSubcategorySelected}>
            <NextLink
              href={urlBuilder({
                universalCategories,
                category: l2_category,
                zip,
              })}
              onClick={closeDialog}
              shallow
            >
              <Body
                strong={isSubcategorySelected}
                size="md"
                as={isAnySubcategorySelected || isCategorySelected ? 'h3' : undefined}
              >
                {name}
              </Body>
            </NextLink>
            {!!isSubcategorySelected && (
              <VisuallyHidden>
                {l2_category.children?.map((l3_category) => (
                  <Body as="h4" key={l3_category.id}>
                    {l3_category.name}
                  </Body>
                ))}
              </VisuallyHidden>
            )}
          </SubcategoryLinkWrapper>
        )
      })}
      <SubcategoryLinkWrapper isSelected={isCategorySelected}>
        <NextLink
          href={urlBuilder({ universalCategories, category, zip })}
          onClick={closeDialog}
          shallow
        >
          <Body strong={isCategorySelected} size="md">
            {`View all ${category.name}`}
          </Body>
        </NextLink>
      </SubcategoryLinkWrapper>
    </AccordionItem>
  ) : (
    <CategoryLinkWrapper>
      <hr />
      <NextLink
        href={urlBuilder({ universalCategories, category, zip })}
        shallow
        onClick={closeDialog}
      >
        <Body strong as="h2">
          {category.name}
        </Body>
      </NextLink>
    </CategoryLinkWrapper>
  )
}

const CategoryLinkWrapper = styled.div`
  padding: 0.25em 0 0.5rem;
  width: 100%;

  a,
  p {
    &:hover,
    &:focus {
      color: ${({ theme }) => theme.color.content.action.primary_hover};
    }
  }

  hr {
    border: 0;
    height: 1px;
    background: ${({ theme }) => theme.color.border.base.neutral.soft};
    margin-top: 0;
    margin-bottom: 1rem;
  }
`

const SubcategoryLinkWrapper = styled.div<{ isSelected: boolean }>`
  margin: 0;
  margin-left: 0.5rem;
  padding: 0.125rem 0.5rem;
  border-left: 2px solid
    ${({ theme, isSelected }) =>
      isSelected ? theme.color.border.action.default : theme.color.border.base.neutral.subtle};

  a {
    color: ${({ theme, isSelected }) =>
      isSelected
        ? theme.color.content.base.neutral.primary
        : theme.color.content.base.neutral.secondary};

    &:hover,
    &:focus {
      color: ${({ theme }) => theme.color.content.action.primary_hover};
    }
  }
`
